import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
// @ts-ignore
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../components/PageContainer';
import { getSegmentColor } from '../utils/segmentColors';
import apiService from '../services/apiService';
import { formatToEasternTime } from '../utils/dateUtils';
import { LoadingState } from '../components/LoadingState';
import EmailPreviewModal from '../components/EmailPreviewModal';

// Email interface
interface Email {
  id: number;
  name: string;
  subject: string | null;
  segment_name: string | null;
  scheduled_date: string;
  is_published: boolean;
}

// Calendar event interface
interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  segment_name: string | null;
}

interface EventComponentProps {
  event: CalendarEvent;
}

// Calendar localizer setup
const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const Calendar: React.FC = () => {
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useUser();
  const navigate = useNavigate();
  const { selectedClientId } = useUser();
  
  // Email preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<Email | null>(null);

  // Fetch emails when client is selected
  useEffect(() => {
    const fetchEmails = async () => {
      if (!selectedClientId || !auth.isAuthenticated) {
        console.log('Calendar: No selectedClientId or not authenticated, skipping data fetch');
        return;
      }

      try {
        setLoading(true);
        console.log('Calendar: Fetching emails with params:', {
          client_id: selectedClientId,
          include_drafts: false,
          future_only: false
        });
        
        const response = await apiService.get<Email[]>('/api/emails', {
          params: {
            client_id: selectedClientId,
            include_drafts: false,
            future_only: false
          }
        });
        
        if (!response.data) {
          console.error('Calendar: No data received from /api/emails');
          setError('No email data received');
          setEmails([]);
          return;
        }
        
        console.log('Calendar: Received emails:', response.data);
        const emailData = response.data;
        setEmails(emailData);
        setError(null);
      } catch (err) {
        console.error('Calendar: Error fetching emails:', err);
        setError('Failed to load emails');
        setEmails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmails();
  }, [selectedClientId, auth.isAuthenticated]);

  // Convert emails to calendar events
  const events: CalendarEvent[] = emails.map(email => ({
    id: email.id,
    title: email.name,
    start: new Date(email.scheduled_date),
    end: new Date(email.scheduled_date),
    segment_name: email.segment_name
  }));

  // Handle view toggle between list and calendar
  const handleViewToggle = (
    _: React.MouseEvent<HTMLElement>,
    newView: 'calendar' | 'list',
  ) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  // Handle opening email preview modal
  const handleOpenEmailPreview = async (email: Email) => {
    try {
      // Fetch full email details including HTML content
      const response = await apiService.get<Email>(`/api/emails/${email.id}`);
      if (response.data) {
        setPreviewEmail(response.data);
        setPreviewModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching email details:', error);
      // Fallback to basic email info if API call fails
      setPreviewEmail(email);
      setPreviewModalOpen(true);
    }
  };
  
  // Handle closing email preview modal
  const handleCloseEmailPreview = () => {
    setPreviewModalOpen(false);
    setPreviewEmail(null);
  };

  // Update eventStyleGetter to use shared getSegmentColor
  const eventStyleGetter = (event: CalendarEvent) => {
    const backgroundColor = getSegmentColor(event.segment_name);
    
    const style = {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block',
      padding: '2px 5px'
    };
    
    return {
      style
    };
  };

  // Handle calendar navigation
  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
  };

  // Simple custom event component 
  const EventComponent: React.FC<EventComponentProps> = ({ event }) => {
    return (
      <div
        style={{ 
          cursor: 'pointer', 
          height: '100%', 
          width: '100%',
          padding: '5px',
          overflow: 'hidden',
          backgroundColor: getSegmentColor(event.segment_name),
          color: 'white',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{ 
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {event.title}
        </div>
        <div style={{ 
          fontSize: '0.85em',
          textTransform: 'capitalize',
          opacity: 0.9,
          fontWeight: 500
        }}>
          {event.segment_name || 'No Segment'}
        </div>
      </div>
    );
  };

  // Format date for display
  const formatScheduledDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    return formatToEasternTime(dateStr);
  };

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Marketing Calendar
      </Typography>

      <Paper elevation={2} className="p-6">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewToggle}
            aria-label="view selector"
            size="small"
          >
            <ToggleButton value="calendar" aria-label="calendar view">
              Calendar
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              List
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        {loading ? (
          <LoadingState message="Loading calendar..." containerClassName="min-h-[600px]" />
        ) : (
          <>
            {view === 'calendar' ? (
              <Box sx={{ height: 800 }}>
                <BigCalendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: '100%' }}
                  onSelectEvent={(event: CalendarEvent) => {
                    const email = emails.find(e => e.id === event.id);
                    if (email) {
                      handleOpenEmailPreview(email);
                    }
                  }}
                  eventPropGetter={eventStyleGetter}
                  date={currentDate}
                  onNavigate={handleNavigate}
                  views={['month']}
                  components={{
                    event: EventComponent
                  }}
                />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Segment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emails
                      .sort((a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime())
                      .map((email) => (
                        <TableRow
                          key={email.id}
                          onClick={() => handleOpenEmailPreview(email)}
                          sx={{ cursor: 'pointer' }}
                          hover
                        >
                          <TableCell>{formatScheduledDate(email.scheduled_date)}</TableCell>
                          <TableCell>{email.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={email.segment_name || 'No Segment'}
                              size="small"
                              sx={{
                                backgroundColor: getSegmentColor(email.segment_name),
                                color: 'white'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={handleCloseEmailPreview}
        email={previewEmail ? {
          name: previewEmail.name,
          subject: previewEmail.subject || '',
          html: previewEmail.html || '',
          scheduled_date: previewEmail.scheduled_date,
          agency_name: '', // We don't have agency name in the Email interface
          segment_name: previewEmail.segment_name,
          existing_tags: [] // We don't have tags in the Email interface
        } : null}
      />
    </PageContainer>
  );
};

export default Calendar;
